'use client';

import { useCallback, useContext, useEffect, useRef, useState, type ChangeEvent, type RefObject } from 'react';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { useQueryClient } from '@tanstack/react-query';
import { ToastContext } from '@/app/contexts';
import { ToastLevel } from '@/components/ui/toast';
import { getGatewaySession } from '@/lib/gateway-fetch';
import {
  EdgeLambdaApiError,
  uploadManualCaseFile,
} from '@/shared/cors-edge-lambda-api';
import { AD_HOC_CASE_DISPLAY_NAME } from '@/shared/ad-hoc-case';
import {
  fetchAdHocManualFileNames,
  isAdHocCase,
  suggestAdHocManualUploadRename,
  validateAdHocManualUploadRename,
} from '@/shared/ad-hoc-evidence';
import { provenanceKeys } from '@/shared/cors-provenance-api';
import { formatUploadErrorDetail } from '@/shared/upload-error-detail';
import type { AdHocFileConflictModalProps } from '@/components/ad-hoc-file-conflict-modal';
import { usePersonalFilesCaseId } from '@/hooks/usePersonalFilesCaseId';

export { isAdHocCase };

type ConflictResolution =
  | { type: 'replace' }
  | { type: 'rename'; fileName: string }
  | { type: 'skip' };

type PendingUpload = {
  file: File;
  fileName: string;
};

export type AdHocCaseFileUploadApi = {
  fileInputRef: RefObject<HTMLInputElement>;
  openFilePicker: () => void;
  handleFileInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  uploadFiles: (files: FileList | File[]) => Promise<void>;
  isUploading: boolean;
  isDragActive: boolean;
  conflictModal: AdHocFileConflictModalProps;
};

type UseAdHocCaseFileUploadOptions = {
  /** Attach window-level drag/drop listeners for file upload. */
  enableDocumentDrop?: boolean;
};

export function useAdHocCaseFileUpload(options: UseAdHocCaseFileUploadOptions = {}) {
  const t = useTimePilotT();
  const { setToast, setToastOpen } = useContext(ToastContext);
  const queryClient = useQueryClient();
  const personalFilesCaseId = usePersonalFilesCaseId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const dragDepthRef = useRef(0);
  /** Held for the entire upload flow (conflict prompts through S3 PUT), not just bytes in flight. */
  const uploadFlowActiveRef = useRef(false);

  const conflictResolverRef = useRef<((resolution: ConflictResolution) => void) | null>(null);
  const [conflictState, setConflictState] = useState<{
    file: File;
    fileName: string;
    canReplace: boolean;
    renameValue: string;
    existingNames: Set<string>;
  } | null>(null);
  const [renameError, setRenameError] = useState<string | null>(null);

  const showToast = useCallback(
    (title: string, description: string, level: ToastLevel) => {
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 4000,
        title,
        description,
        level,
      });
    },
    [setToast, setToastOpen],
  );

  const finishConflict = useCallback((resolution: ConflictResolution) => {
    conflictResolverRef.current?.(resolution);
    conflictResolverRef.current = null;
    setConflictState(null);
    setRenameError(null);
  }, []);

  const promptConflict = useCallback(
    (
      file: File,
      fileName: string,
      canReplace: boolean,
      existingNames: Set<string>,
    ): Promise<ConflictResolution> =>
      new Promise((resolve) => {
        conflictResolverRef.current = resolve;
        setRenameError(null);
        const takenNames = new Set([...existingNames, fileName]);
        setConflictState({
          file,
          fileName,
          canReplace,
          renameValue: suggestAdHocManualUploadRename(fileName, takenNames),
          existingNames: takenNames,
        });
      }),
    [],
  );

  const resolveUploadQueue = useCallback(
    async (
      files: File[],
      existingNames: Set<string>,
    ): Promise<PendingUpload[]> => {
      const resolved: PendingUpload[] = [];
      const namesInBatch = new Set<string>();

      for (const file of files) {
        let targetName = file.name.trim();
        if (!targetName) continue;

        let settled = false;
        while (!settled) {
          const existsOnServer = existingNames.has(targetName);
          const duplicateInBatch = namesInBatch.has(targetName);

          if (!existsOnServer && !duplicateInBatch) {
            resolved.push({ file, fileName: targetName });
            namesInBatch.add(targetName);
            settled = true;
            continue;
          }

          const resolution = await promptConflict(
            file,
            targetName,
            existsOnServer,
            new Set([...existingNames, ...namesInBatch]),
          );

          if (resolution.type === 'skip') {
            settled = true;
            continue;
          }

          if (resolution.type === 'replace') {
            resolved.push({ file, fileName: targetName });
            namesInBatch.add(targetName);
            settled = true;
            continue;
          }

          targetName = resolution.fileName.trim();
        }
      }

      return resolved;
    },
    [promptConflict],
  );

  const uploadResolvedFiles = useCallback(
    async (queue: PendingUpload[], session: { cellId: string; accessToken: string }) => {
      const failed: string[] = [];
      let firstErrorDetail: string | null = null;

      for (const { file, fileName } of queue) {
        try {
          if (!personalFilesCaseId) {
            throw new EdgeLambdaApiError('My Files case is not resolved', 401);
          }
          await uploadManualCaseFile({
            cellId: session.cellId,
            accessToken: session.accessToken,
            file,
            fileName,
            caseId: personalFilesCaseId,
          });
        } catch (error) {
          failed.push(fileName);
          if (!firstErrorDetail && error instanceof EdgeLambdaApiError) {
            firstErrorDetail = formatUploadErrorDetail(error) ?? error.message;
            console.error('Ad Hoc file upload failed:', firstErrorDetail, error.status ?? '');
          }
        }
      }

      return { failed, firstErrorDetail, total: queue.length };
    },
    [personalFilesCaseId],
  );

  const refreshProvenanceAfterUpload = useCallback(async () => {
    await Promise.all([
      queryClient.refetchQueries({ queryKey: provenanceKeys.tree() }),
      queryClient.refetchQueries({ queryKey: provenanceKeys.files() }),
    ]);
  }, [queryClient]);

  const uploadFiles = useCallback(
    async (files: FileList | File[]) => {
      if (uploadFlowActiveRef.current) {
        return;
      }
      uploadFlowActiveRef.current = true;

      try {
        const fileArray = Array.from(files).filter((f) => f.size > 0 && f.name.trim() !== '');
        if (!fileArray.length) return;

        const session = await getGatewaySession();
        if (!session) {
          showToast(t('upload.failed'), t('upload.noSession'), ToastLevel.ERROR);
          return;
        }
        if (!personalFilesCaseId) {
          showToast(
            t('upload.failed'),
            t('upload.myFilesFailed'),
            ToastLevel.ERROR,
          );
          return;
        }

        let existingNames: Set<string>;
        try {
          existingNames = await fetchAdHocManualFileNames({
            cellId: session.cellId,
            accessToken: session.accessToken,
            caseId: personalFilesCaseId,
          });
        } catch (error) {
          showToast(
            t('upload.failed'),
            t('upload.checkManualFailed', { detail: (error as Error).message }),
            ToastLevel.ERROR,
          );
          return;
        }

        const queue = await resolveUploadQueue(fileArray, existingNames);
        if (!queue.length) {
          return;
        }

        setIsUploading(true);
        const { failed, firstErrorDetail, total } = await uploadResolvedFiles(queue, session);
        setIsUploading(false);

        if (failed.length === 0) {
          showToast(t('upload.complete'), total === 1 ? t('upload.completeOne', { name: queue[0].fileName, caseName: AD_HOC_CASE_DISPLAY_NAME }) : t('upload.completeMany', { count: total, caseName: AD_HOC_CASE_DISPLAY_NAME }), ToastLevel.SUCCESS);
          await refreshProvenanceAfterUpload();
          return;
        }

        if (failed.length === total) {
          const description =
            total === 1
              ? firstErrorDetail
                ? t('upload.couldNotUploadOneDetail', { name: failed[0], detail: firstErrorDetail })
                : t('upload.couldNotUploadOne', { name: failed[0] })
              : firstErrorDetail
                ? t('upload.couldNotUploadManyDetail', { detail: firstErrorDetail })
                : t('upload.couldNotUploadMany');
          showToast(t('upload.failed'), description, ToastLevel.ERROR);
          return;
        }

        showToast(
          t('upload.partial'),
          t('upload.partialShort', { uploaded: total - failed.length, total, failed: failed.join(', ') }),
          ToastLevel.WARNING,
        );
        await refreshProvenanceAfterUpload();
      } finally {
        uploadFlowActiveRef.current = false;
      }
    },
    [personalFilesCaseId, refreshProvenanceAfterUpload, resolveUploadQueue, showToast, t, uploadResolvedFiles],
  );

  const openFilePicker = useCallback(() => {
    if (uploadFlowActiveRef.current || isUploading || conflictState) return;
    fileInputRef.current?.click();
  }, [conflictState, isUploading]);

  const handleFileInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const selected = event.target.files;
      if (selected?.length && !uploadFlowActiveRef.current) {
        void uploadFiles(selected);
      }
      event.target.value = '';
    },
    [uploadFiles],
  );

  const handleConflictReplace = useCallback(() => {
    finishConflict({ type: 'replace' });
  }, [finishConflict]);

  const handleConflictCancel = useCallback(() => {
    finishConflict({ type: 'skip' });
  }, [finishConflict]);

  const handleConflictRename = useCallback(() => {
    if (!conflictState) return;
    const trimmed = conflictState.renameValue.trim();
    const validationError = validateAdHocManualUploadRename(
      trimmed,
      conflictState.fileName,
      conflictState.existingNames,
    );
    if (validationError) {
      setRenameError(validationError);
      return;
    }
    finishConflict({ type: 'rename', fileName: trimmed });
  }, [conflictState, finishConflict]);

  const handleRenameValueChange = useCallback((value: string) => {
    setRenameError(null);
    setConflictState((prev) => (prev ? { ...prev, renameValue: value } : prev));
  }, []);

  useEffect(() => {
    if (!options.enableDocumentDrop) return;

    const hasFiles = (event: DragEvent) =>
      Array.from(event.dataTransfer?.types ?? []).includes('Files');

    const onDragEnter = (event: DragEvent) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      dragDepthRef.current += 1;
      setIsDragActive(true);
    };

    const onDragOver = (event: DragEvent) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy';
      }
    };

    const onDragLeave = (event: DragEvent) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
      if (dragDepthRef.current === 0) {
        setIsDragActive(false);
      }
    };

    const onDrop = (event: DragEvent) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      dragDepthRef.current = 0;
      setIsDragActive(false);
      if (uploadFlowActiveRef.current || isUploading || conflictState) return;
      const dropped = event.dataTransfer?.files;
      if (dropped?.length) {
        void uploadFiles(dropped);
      }
    };

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);

    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
      dragDepthRef.current = 0;
      setIsDragActive(false);
    };
  }, [conflictState, options.enableDocumentDrop, isUploading, uploadFiles]);

  const conflictModal: AdHocFileConflictModalProps = {
    isOpen: conflictState !== null,
    fileName: conflictState?.fileName ?? '',
    canReplace: conflictState?.canReplace ?? false,
    renameValue: conflictState?.renameValue ?? '',
    renameError,
    onRenameValueChange: handleRenameValueChange,
    onReplace: handleConflictReplace,
    onRename: handleConflictRename,
    onCancel: handleConflictCancel,
  };

  return {
    fileInputRef,
    openFilePicker,
    handleFileInputChange,
    uploadFiles,
    isUploading,
    isDragActive,
    conflictModal,
  };
}
